import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Avatar } from '@mui/material';

const avatarColor = (name = "") => {
  const c = ['#00C2B3', '#0088A3', '#007A70', '#005A6E'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return c[Math.abs(h) % c.length];
};

// Global notification state — used via hook
let globalAddNotif = null;

export function triggerNotification(notif) {
  if (globalAddNotif) globalAddNotif(notif);
}

function NotificationToast({ notif, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, []);

  return (
    <Box
      onClick={onClose}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: '#0E1318', border: '1px solid #1A2228',
        borderLeft: '3px solid #00C2B3',
        borderRadius: '12px',
        px: 2, py: 1.5,
        cursor: 'pointer',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        minWidth: 280, maxWidth: 340,
        animation: 'slideIn 0.25s ease',
        '@keyframes slideIn': {
          from: { transform: 'translateX(100%)', opacity: 0 },
          to: { transform: 'translateX(0)', opacity: 1 },
        },
      }}
    >
      <Avatar sx={{
        width: 32, height: 32, fontSize: 12, fontWeight: 700,
        bgcolor: avatarColor(notif.from), flexShrink: 0,
      }}>
        {notif.from?.[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 600,
          color: '#F0F4F8', lineHeight: 1.2,
        }}>
          {notif.from}
          {notif.channel && (
            <Box component="span" sx={{ color: '#3A4A58', fontWeight: 400 }}>
              {' '}in {notif.channel}
            </Box>
          )}
        </Typography>
        <Typography sx={{
          fontFamily: "'Outfit', sans-serif", fontSize: 12, color: '#4A5A68',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {notif.message || '📎 Sent a file'}
        </Typography>
      </Box>
      <Box sx={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#2A3A48', flexShrink: 0,
      }}>
        now
      </Box>
    </Box>
  );
}

export default function NotificationSystem() {
  const [notifications, setNotifications] = useState([]);
  const idRef = useRef(0);

  const addNotif = useCallback((notif) => {
    const id = ++idRef.current;
    setNotifications(prev => [...prev.slice(-4), { ...notif, id }]);
  }, []);

  useEffect(() => {
    globalAddNotif = addNotif;
    return () => { globalAddNotif = null; };
  }, [addNotif]);

  const remove = (id) => setNotifications(prev => prev.filter(n => n.id !== id));

  return (
    <Box sx={{
      position: 'fixed', top: 76, right: 16, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 1,
      pointerEvents: 'none',
    }}>
      {notifications.map(n => (
        <Box key={n.id} sx={{ pointerEvents: 'all' }}>
          <NotificationToast notif={n} onClose={() => remove(n.id)} />
        </Box>
      ))}
    </Box>
  );
}